import React, { useEffect, useState, useMemo } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { getGroupDisplayNames, getUserInfo } from '../../services/authService-sighup';



interface PowerBIReportProps {
  workspaceId: string;
  reportId: string;
  pageName: string;
}

const PowerBIReport: React.FC<PowerBIReportProps> = ({ workspaceId, reportId, pageName }) => {
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedToken, setEmbedToken] = useState('');
  const [reportIdState, setReportIdState] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showDetails, setShowDetails] = useState(false);


  useEffect(() => {
    let isMounted = true;

    const getEmbedToken = async () => {
      try {
        const baseApiUrl = process.env.REACT_APP_POWERBI_EMBED_API_URL;
        const apiUrl = `${baseApiUrl}/api/getEmbedToken`;

        const userInfo = getUserInfo();
        const groupNames = await getGroupDisplayNames();

        const payload = {
          workspaceId,
          reportId,
          username: userInfo?.email || '',
          userGroups: groupNames,
        };

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          let friendlyMessage = 'Failed to load Power BI report.';
          if (data?.error === 'User does not belong to any authorized security groups') {
            friendlyMessage = 'Access denied : You are not part of any authorized user groups.';
          } else if (data?.error?.includes('workspaceId') || data?.message?.includes('workspaceId')) {
            friendlyMessage = 'Configuration error : Missing required workspace or report information.';
          } else if (data?.statusCode === 400 && data?.details?.powerBIErrorCode === 'InvalidRequest') {
            friendlyMessage = 'Invalid workspace/report ID. Please contact the administrator.';
          } else if (data?.message) {
            friendlyMessage = data.message;
          }
          throw new Error(friendlyMessage);
        }

        if (isMounted && data.embedToken && data.embedUrl && data.reportId) {
          setEmbedToken(data.embedToken);
          setEmbedUrl(data.embedUrl);
          setReportIdState(data.reportId);
        }
      } catch (error: any) {
        console.error('Error acquiring embed token:', error);
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load Power BI report.');
        }
      }
    };
    getEmbedToken();
    return () => {
      isMounted = false;
    };
  }, [workspaceId, reportId]);

  const embedConfig = useMemo(() => ({
    type: 'report',
    tokenType: models.TokenType.Embed,
    accessToken: embedToken,
    embedUrl: embedUrl,
    id: reportIdState,
    pageName: pageName,
    settings: {
      panes: { filters: { visible: true } },
      navContentPaneEnabled: false,
      background: models.BackgroundType.Transparent,
    },
  }), [embedToken, embedUrl, reportIdState, pageName]);

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-white">
        <div className="text-gray-500 p-4">
          <div className="text-center max-w-lg mx-auto">
            <p className="text-lg font-semibold">
              No Data Available
              <sup className="ml-2 text-sm text-purple-600 cursor-pointer">
                <span onClick={() => setShowDetails(!showDetails)}>
                  {showDetails ? "hide details" : "see details"}
                </span>
              </sup>
            </p>
            {showDetails && (
              <pre className="mt-4 whitespace-pre-wrap text-gray-600 text-left font-normal">
                {typeof errorMessage === 'string'
                  ? errorMessage
                  : errorMessage || 'Unknown error'}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }



  if (!embedToken || !embedUrl || !reportIdState) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-white">
        <div className="text-center text-gray-600">
          <p className="text-xl font-semibold">Loading Power BI Visual...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="relative w-full h-full overflow-hidden">
      <PowerBIEmbed embedConfig={embedConfig} cssClassName="w-full h-full" />
    </div>
  );
};

export default PowerBIReport;
